import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { certificatesApi, certificateTemplatesApi, personnelApi, teachersApi, attendanceRegistersApi, pdvApi, trainingCertificatesApi, trainingTypesApi, ocrApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Award, Plus, FileCheck, Clock, CheckCircle, Search, Printer, Upload, Send } from 'lucide-react';

export default function CertificatesPage() {
  const { user } = useAuth();
  const isUL = user?.role === 'gerente' || user?.role === 'collaborator';
  const [certificates, setCertificates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [certDialogOpen, setCertDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);

  // Upload PDF attestato (con OCR)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    personnel_id: '',
    training_type_id: '',
    issue_date: '',
    expiry_date: '',
  });
  
  const [certForm, setCertForm] = useState({
    template_id: '',
    personnel_id: '',
    teacher_id: '',
    course_name: '',
    course_date: new Date().toISOString().split('T')[0],
    course_location: '',
    duration_hours: ''
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    course_type: '',
    duration_hours: '',
    validity_years: 5
  });

  const [registerForm, setRegisterForm] = useState({
    teacher_id: '',
    course_name: '',
    course_date: new Date().toISOString().split('T')[0],
    course_location: '',
    duration_hours: '',
    participant_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [certsRes, templatesRes, registersRes, personnelRes, teachersRes, pdvRes, ttypesRes] = await Promise.all([
        certificatesApi.getAll(),
        certificateTemplatesApi.getAll(),
        attendanceRegistersApi.getAll(),
        personnelApi.getAll(),
        teachersApi.getAll(),
        pdvApi.getAll(),
        trainingTypesApi.getAll().catch(() => ({ data: [] })),
      ]);
      setCertificates(certsRes.data);
      setTemplates(templatesRes.data);
      setRegisters(registersRes.data);
      setPersonnel(personnelRes.data);
      setTeachers(teachersRes.data);
      setPdvList(pdvRes.data);
      setTrainingTypes(ttypesRes.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // ---- Upload PDF attestato con OCR e abbinamento al personale ----
  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadFile(f);
    setOcrLoading(true);
    try {
      const r = await ocrApi.extract(f, 'training_certificate');
      const data = r.data || {};
      // Try to match personnel by fiscal code or name (case-insensitive)
      let matchedPersonnelId = '';
      if (data.fiscal_code) {
        const p = personnel.find(x => (x.fiscal_code || '').toUpperCase() === data.fiscal_code.toUpperCase());
        if (p) matchedPersonnelId = p.id;
      }
      if (!matchedPersonnelId && data.full_name) {
        const fn = data.full_name.toLowerCase();
        const p = personnel.find(x => `${x.first_name || ''} ${x.last_name || ''}`.toLowerCase().includes(fn) ||
                                       fn.includes(`${(x.first_name || '').toLowerCase()} ${(x.last_name || '').toLowerCase()}`));
        if (p) matchedPersonnelId = p.id;
      }
      // Match training type by name
      let matchedTypeId = '';
      if (data.course_name && trainingTypes.length) {
        const cn = data.course_name.toLowerCase();
        const t = trainingTypes.find(x => (x.name || '').toLowerCase().includes(cn) || cn.includes((x.name || '').toLowerCase()));
        if (t) matchedTypeId = t.id;
      }
      setUploadForm((prev) => ({
        ...prev,
        personnel_id: matchedPersonnelId || prev.personnel_id,
        training_type_id: matchedTypeId || prev.training_type_id,
        issue_date: data.issue_date || prev.issue_date,
        expiry_date: data.expiry_date || prev.expiry_date,
      }));
      if (matchedPersonnelId) {
        toast.success('Dipendente abbinato automaticamente dal PDF');
      } else {
        toast.info('Seleziona manualmente il dipendente a cui abbinare l\'attestato');
      }
    } catch (err) {
      toast.warning('OCR non disponibile: compila manualmente i campi');
    } finally {
      setOcrLoading(false);
    }
  };

  const handleUploadAttestato = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error('Seleziona un file PDF'); return; }
    if (!uploadForm.personnel_id) { toast.error('Seleziona il dipendente'); return; }
    if (!uploadForm.training_type_id) { toast.error('Seleziona il tipo di corso'); return; }
    try {
      await trainingCertificatesApi.upload(uploadFile, uploadForm);
      toast.success('Attestato caricato. Le scadenze e gli alert sono attivi.');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadForm({ personnel_id: '', training_type_id: '', issue_date: '', expiry_date: '' });
      loadData();
    } catch (err) {
      toast.error('Errore: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreateCertificate = async (e) => {
    e.preventDefault();
    try {
      const response = await certificatesApi.create(certForm);
      toast.success(`Attestato creato: ${response.data.certificate_code}`);
      setCertDialogOpen(false);
      setCertForm({ template_id: '', personnel_id: '', teacher_id: '', course_name: '', course_date: new Date().toISOString().split('T')[0], course_location: '', duration_hours: '' });
      loadData();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await certificateTemplatesApi.create(templateForm);
      toast.success('Modello creato');
      setTemplateDialogOpen(false);
      setTemplateForm({ name: '', description: '', course_type: '', duration_hours: '', validity_years: 5 });
      loadData();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  const handleCreateRegister = async (e) => {
    e.preventDefault();
    try {
      await attendanceRegistersApi.create(registerForm);
      toast.success('Registro creato');
      setRegisterDialogOpen(false);
      setRegisterForm({ teacher_id: '', course_name: '', course_date: new Date().toISOString().split('T')[0], course_location: '', duration_hours: '', participant_ids: [] });
      loadData();
    } catch (error) {
      toast.error('Errore nella creazione');
    }
  };

  const handleVerifyCertificate = async () => {
    if (!verifyCode) return;
    try {
      const response = await certificatesApi.verify(verifyCode);
      setVerifyResult(response.data);
    } catch (error) {
      setVerifyResult({ valid: false, message: 'Errore nella verifica' });
    }
  };

  const handleIssueCertificate = async (certId) => {
    try {
      await certificatesApi.issue(certId);
      toast.success('Attestato emesso');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handlePrintRegister = async (registerId) => {
    try {
      const response = await attendanceRegistersApi.getPrintHtml(registerId);
      const newWindow = window.open('', '_blank');
      newWindow.document.write(response.data);
      newWindow.document.close();
      await attendanceRegistersApi.markPrinted(registerId);
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const handleSubmitRegister = async (registerId) => {
    try {
      await attendanceRegistersApi.submit(registerId);
      toast.success('Registro inviato');
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const getPersonName = (id) => {
    const p = personnel.find(x => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : 'N/D';
  };

  const getTeacherName = (id) => {
    const t = teachers.find(x => x.id === id);
    return t?.name || 'N/D';
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-800',
    register_submitted: 'bg-blue-100 text-blue-800',
    issued: 'bg-green-100 text-green-800',
    revoked: 'bg-red-100 text-red-800'
  };

  const statusLabels = {
    pending: 'In attesa',
    register_submitted: 'Registro inviato',
    issued: 'Emesso',
    revoked: 'Revocato'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // v1.3.22: nascondi i modelli attestato predefiniti/backup (is_builtin) nella gestione.
  const visibleTemplates = templates.filter((t) => !t.is_builtin);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl">Gestione Attestati</h1>
          <p className="text-muted-foreground mt-1">Attestati, modelli e registri presenze</p>
        </div>
      </div>

      <Tabs defaultValue="certificates" className="space-y-6">
        <TabsList>
          <TabsTrigger value="certificates">Attestati ({certificates.length})</TabsTrigger>
          {!isUL && <TabsTrigger value="templates">Modelli ({visibleTemplates.length})</TabsTrigger>}
          {!isUL && <TabsTrigger value="registers">Registri ({registers.length})</TabsTrigger>}
          <TabsTrigger value="verify">Verifica</TabsTrigger>
        </TabsList>

        {/* CERTIFICATES TAB */}
        <TabsContent value="certificates" className="space-y-4">
          <div className="flex justify-end gap-2">
            {/* Upload PDF Attestato (OCR + abbinamento personale) */}
            <Button
              variant="outline"
              onClick={() => setUploadDialogOpen(true)}
              data-testid="upload-attestato-pdf-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Carica Attestato PDF
            </Button>
            {!isUL && (
              <Dialog open={certDialogOpen} onOpenChange={setCertDialogOpen}>
                <DialogTrigger asChild>
                  <Button><Plus className="w-4 h-4 mr-2" />Nuovo Attestato</Button>
                </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Crea Attestato</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateCertificate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Modello Attestato *</Label>
                    <Select value={certForm.template_id} onValueChange={(v) => {
                      const tpl = templates.find(t => t.id === v);
                      setCertForm({...certForm, template_id: v, course_name: tpl?.name || ''});
                    }}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {visibleTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Dipendente *</Label>
                    <Select value={certForm.personnel_id} onValueChange={(v) => setCertForm({...certForm, personnel_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {personnel.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Docente</Label>
                    <Select value={certForm.teacher_id} onValueChange={(v) => setCertForm({...certForm, teacher_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data Corso *</Label>
                      <Input type="date" value={certForm.course_date} onChange={(e) => setCertForm({...certForm, course_date: e.target.value})} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Ore</Label>
                      <Input type="number" value={certForm.duration_hours} onChange={(e) => setCertForm({...certForm, duration_hours: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Luogo</Label>
                    <Input value={certForm.course_location} onChange={(e) => setCertForm({...certForm, course_location: e.target.value})} />
                  </div>
                  <Button type="submit" className="w-full" disabled={!certForm.template_id || !certForm.personnel_id}>Crea Attestato</Button>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>

          {/* Upload PDF dialog */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogContent className="max-w-xl w-[95vw]" data-testid="upload-attestato-dialog">
              <DialogHeader>
                <DialogTitle>Carica Attestato PDF</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUploadAttestato} className="space-y-4">
                <div className="space-y-2">
                  <Label>File PDF *</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    data-testid="upload-attestato-file"
                  />
                  {ocrLoading && (
                    <p className="text-xs text-muted-foreground">Lettura automatica del PDF in corso (OCR)...</p>
                  )}
                  {uploadFile && !ocrLoading && (
                    <p className="text-xs text-green-700">File: {uploadFile.name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Dipendente *</Label>
                  <Select
                    value={uploadForm.personnel_id}
                    onValueChange={(v) => setUploadForm({ ...uploadForm, personnel_id: v })}
                  >
                    <SelectTrigger data-testid="upload-attestato-personnel"><SelectValue placeholder="Abbina al dipendente" /></SelectTrigger>
                    <SelectContent>
                      {personnel.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.first_name} {p.last_name} {p.fiscal_code ? `(${p.fiscal_code})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo Corso *</Label>
                  <Select
                    value={uploadForm.training_type_id}
                    onValueChange={(v) => setUploadForm({ ...uploadForm, training_type_id: v })}
                  >
                    <SelectTrigger data-testid="upload-attestato-type"><SelectValue placeholder="Seleziona il corso" /></SelectTrigger>
                    <SelectContent>
                      {trainingTypes.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Data Emissione</Label>
                    <Input
                      type="date"
                      value={uploadForm.issue_date}
                      onChange={(e) => setUploadForm({ ...uploadForm, issue_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Scadenza (auto se vuoto)</Label>
                    <Input
                      type="date"
                      value={uploadForm.expiry_date}
                      onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })}
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={ocrLoading || !uploadFile || !uploadForm.personnel_id || !uploadForm.training_type_id} data-testid="upload-attestato-submit">
                  <Upload className="w-4 h-4 mr-2" />
                  Carica e attiva le scadenze
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Il file viene salvato sia tra gli attestati sia tra i documenti del dipendente.
                  Le scadenze e gli alert vengono attivati automaticamente.
                </p>
              </form>
            </DialogContent>
          </Dialog>

          <div className="space-y-3">
            {certificates.map((cert, i) => (
              <motion.div key={cert.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Award className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{cert.course_name}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[cert.status]}`}>{statusLabels[cert.status]}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{getPersonName(cert.personnel_id)} • {cert.course_date}</p>
                          <p className="text-xs font-mono text-muted-foreground">Codice: {cert.certificate_code}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {cert.status === 'pending' && (
                          <Button size="sm" onClick={() => handleIssueCertificate(cert.id)}>
                            <CheckCircle className="w-4 h-4 mr-1" />Emetti
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        {/* TEMPLATES TAB */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="w-4 h-4 mr-2" />Nuovo Modello</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Crea Modello Attestato</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input value={templateForm.name} onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Corso *</Label>
                    <Select value={templateForm.course_type} onValueChange={(v) => setTemplateForm({...templateForm, course_type: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sicurezza_generale">Sicurezza Generale</SelectItem>
                        <SelectItem value="antincendio">Antincendio</SelectItem>
                        <SelectItem value="primo_soccorso">Primo Soccorso</SelectItem>
                        <SelectItem value="haccp">HACCP</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Durata (ore)</Label>
                      <Input type="number" value={templateForm.duration_hours} onChange={(e) => setTemplateForm({...templateForm, duration_hours: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Validità (anni)</Label>
                      <Input type="number" value={templateForm.validity_years} onChange={(e) => setTemplateForm({...templateForm, validity_years: parseInt(e.target.value)})} />
                    </div>
                  </div>
                  <Button type="submit" className="w-full">Crea Modello</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {visibleTemplates.map(tpl => (
              <Card key={tpl.id}>
                <CardContent className="p-4">
                  <h3 className="font-semibold">{tpl.name}</h3>
                  <p className="text-sm text-muted-foreground">{tpl.course_type}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    {tpl.duration_hours && <span>{tpl.duration_hours} ore</span>}
                    <span>Validità: {tpl.validity_years} anni</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* REGISTERS TAB */}
        <TabsContent value="registers" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
              <DialogTrigger asChild>
                <Button><Plus className="w-4 h-4 mr-2" />Nuovo Registro</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Crea Registro Presenze</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Docente *</Label>
                    <Select value={registerForm.teacher_id} onValueChange={(v) => setRegisterForm({...registerForm, teacher_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Nome Corso *</Label>
                    <Input value={registerForm.course_name} onChange={(e) => setRegisterForm({...registerForm, course_name: e.target.value})} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input type="date" value={registerForm.course_date} onChange={(e) => setRegisterForm({...registerForm, course_date: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Ore</Label>
                      <Input type="number" value={registerForm.duration_hours} onChange={(e) => setRegisterForm({...registerForm, duration_hours: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Luogo</Label>
                    <Input value={registerForm.course_location} onChange={(e) => setRegisterForm({...registerForm, course_location: e.target.value})} />
                  </div>
                  <Button type="submit" className="w-full" disabled={!registerForm.teacher_id || !registerForm.course_name}>Crea Registro</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-3">
            {registers.map((reg, i) => (
              <motion.div key={reg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{reg.course_name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            reg.status === 'submitted' ? 'bg-green-100 text-green-800' :
                            reg.status === 'printed' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>{reg.status}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{getTeacherName(reg.teacher_id)} • {reg.course_date}</p>
                        <p className="text-xs font-mono text-muted-foreground">{reg.register_code}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => handlePrintRegister(reg.id)}>
                          <Printer className="w-4 h-4 mr-1" />Stampa
                        </Button>
                        {reg.status !== 'submitted' && (
                          <Button size="sm" onClick={() => handleSubmitRegister(reg.id)}>
                            <Send className="w-4 h-4 mr-1" />Invia
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        {/* VERIFY TAB */}
        <TabsContent value="verify" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-4">Verifica Attestato</h3>
              <div className="flex gap-2 max-w-md">
                <Input
                  placeholder="Inserisci codice attestato (es. CEN-CERT-2024-XXXX)"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                />
                <Button onClick={handleVerifyCertificate}>
                  <Search className="w-4 h-4 mr-2" />Verifica
                </Button>
              </div>
              
              {verifyResult && (
                <div className={`mt-4 p-4 rounded-lg ${verifyResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {verifyResult.valid ? (
                    <div>
                      <div className="flex items-center gap-2 text-green-700 font-semibold">
                        <CheckCircle className="w-5 h-5" />
                        Attestato Valido
                      </div>
                      <div className="mt-2 text-sm space-y-1">
                        <p><strong>Corso:</strong> {verifyResult.course_name}</p>
                        <p><strong>Partecipante:</strong> {verifyResult.person_name}</p>
                        <p><strong>Data:</strong> {verifyResult.course_date}</p>
                        {verifyResult.expiry_date && <p><strong>Scadenza:</strong> {verifyResult.expiry_date}</p>}
                      </div>
                    </div>
                  ) : (
                    <div className="text-red-700">
                      <strong>Non valido:</strong> {verifyResult.message}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
